import { Injectable, Optional } from '@angular/core';
import { FormlyFieldConfig, FormlyFieldProps } from '@ngx-formly/core';
import {
  camelCase,
  clone,
  cloneDeep,
  compact,
  defaults,
  defaultsDeep,
  flatMap,
  forEach,
  get,
  identity,
  isString,
  join,
  map,
  mapValues,
  mergeWith,
  reduce,
  some,
  split,
} from 'lodash';
import { DynamicFormsExtensionsService } from './dynamic-forms-extensions.service';
import { ProposalPermissions } from '@lockton-apps/shared/interfaces';

interface FormlyFieldConfigWithExtensions
  extends FormlyFieldConfig<FormlyFieldProps & { [key: string]: unknown }> {
  key: string;
  hooksId: string;
  parsersId: string;
  expressionsId: string | string[];
  extensionIds: string[];
  fieldGroup: FormlyFieldConfigWithExtensions[];
  validatorIds: string | string[];
  fieldGroupValidatorId: string;
}

interface DynamicFormBuilderOptions {
  readonly?: boolean;
  print?: boolean;
  modelPrefix?: string;
  permissions?: ProposalPermissions;
}

@Injectable()
export class DynamicFormBuilderService {
  private prefixer = (expression: any) => identity(expression);

  constructor(
    @Optional()
    private extensionService: DynamicFormsExtensionsService
  ) {}

  buildFields(
    config: any,
    data: any,
    stepIndex: number,
    options: DynamicFormBuilderOptions
  ): FormlyFieldConfig[] {
    const globalReadonly = get(options, 'readonly', false);
    const print = get(options, 'print', false);
    const modelPrefix = get(options, 'modelPrefix');

    const [main, partialId] = split(config.id, '_');
    const baseId = `${camelCase(main)}${partialId || ''}`;

    if (modelPrefix) {
      this.setPrefixer(modelPrefix);
    }

    return compact(
      map(config.fieldGroups, (fieldGroup: any, sectionIndex: number) => {
        const groupPermissionReadonly: boolean = !!get(
          options,
          ['permissions', 'readonly', fieldGroup.id, 'status'],
          false
        );
        const readonly: boolean = globalReadonly || groupPermissionReadonly;

        if (!fieldGroup.id) {
          return;
        }

        let groupClassName: string = 'form__group';

        if (!sectionIndex) {
          groupClassName += ' form__group--first';
        }

        if (this.extensionService) {
          fieldGroup = this.applyExpressionsToFieldGroup(fieldGroup);
        }

        return {
          className: groupClassName,
          fieldGroupClassName: fieldGroup.classes?.group,
          wrappers: ['panel'],
          renderFormlyFieldElement: false,
          props: defaultsDeep(
            {
              id: `${baseId}.${fieldGroup.id}`,
              index: `${stepIndex + 1}.${sectionIndex + 1}`,
              className: fieldGroup.className,
              label: fieldGroup.label,
              description: fieldGroup.description,
              tooltip: clone(fieldGroup.tooltip),
              notes: fieldGroup.notes,
              icon: fieldGroup.icon,
              iconColor: fieldGroup.iconColor,
              iconBackgroundColor: fieldGroup.iconBackgroundColor,
              readonly,
              print,
              permissions: reduce(
                options.permissions,
                (permissions, permission, name) => {
                  if (!permission) {
                    return permissions;
                  }

                  return defaults(permissions, {
                    [name]: permission[fieldGroup.id],
                  });
                },
                {}
              ),
            },
            fieldGroup.props
          ),
          expressions:
            !!fieldGroup.expressions &&
            this.prefixExpressions(fieldGroup.expressions),
          fieldGroup: flatMap(cloneDeep(fieldGroup.fields), (_field) => {
            const key = `${baseId}.${fieldGroup.id}`;

            if (_field.fieldGroup) {
              return this.handleFieldGroup(_field, key, data, readonly, print);
            }
            return this.handleField(_field, key, data, readonly, print);
          }),
        };
      })
    );
  }

  private handleFieldGroup(
    field: FormlyFieldConfigWithExtensions,
    path: string,
    data: any,
    readonly: boolean,
    print: boolean
  ): FormlyFieldConfigWithExtensions {
    const fieldGroup = cloneDeep(field);
    let fieldGroupWithExtensions = this.applyExtensions(fieldGroup, data);
    fieldGroupWithExtensions = defaults(
      {
        className: join(
          compact([
            fieldGroupWithExtensions.className,
            'form__field',
            readonly && 'lui-form-field--readonly',
          ]),
          ' '
        ),
        expressions:
          !!fieldGroupWithExtensions.expressions &&
          this.prefixExpressions(fieldGroupWithExtensions.expressions),
      },
      fieldGroupWithExtensions
    );

    fieldGroupWithExtensions.fieldGroup = reduce(
      field.fieldGroup,
      (acc, _field) => {
        if (_field.fieldGroup) {
          return [
            ...acc,
            this.handleFieldGroup(_field, path, data, readonly, print),
          ];
        }
        return [
          ...acc,
          ...this.handleField(_field, path, data, readonly, print),
        ];
      },
      [] as FormlyFieldConfigWithExtensions[]
    );
    return fieldGroupWithExtensions;
  }

  private handleField(
    field: FormlyFieldConfigWithExtensions,
    path: string,
    data: any,
    readonly: boolean,
    print: boolean
  ): FormlyFieldConfigWithExtensions[] {
    const key = `${path}.${field.key}`;

    switch (field.type) {
      case 'abstractGroup':
        return this.handleAbstractGroup(field, path, data, readonly, print);

      case 'template':
        return [this.handleTemplate(field)];

      case 'external': {
        const fields = get(
          field,
          'props.fields',
          []
        ) as FormlyFieldConfigWithExtensions[];

        field.props = defaults(
          {
            fields: map(fields, (innerField) => {
              if (!innerField.key) {
                return innerField;
              }

              return this.enhancedField(
                innerField.key,
                innerField,
                data,
                readonly,
                print
              );
            }),
          },
          field.props
        );

        return [this.enhancedField(key, field, data, readonly, print)];
      }

      case 'dynamic': {
        const fields = get(
          field,
          'props.fields',
          []
        ) as FormlyFieldConfigWithExtensions[];

        const innerModel = get(data, join([path, field.key], '.'), {});

        field.props = defaults(
          {
            fields: map(fields, (innerField) => {
              if (!innerField.key) {
                return innerField;
              }

              return this.enhancedField(
                innerField.key,
                innerField,
                data,
                readonly,
                print,
                innerModel || {}
              );
            }),
          },
          field.props
        );

        return [this.enhancedField(key, field, data, readonly, print)];
      }

      case 'calc-table': {
        const rows = get(field, 'props.tableRows', []) as any;

        const enhancedRows = map(rows, (row) => {
          const fields = get(
            row,
            'fields',
            []
          ) as FormlyFieldConfigWithExtensions[];

          const innerModel = get(data, join([path, field.key], '.'), {});

          return defaults(
            {
              fields: map(fields, (innerField) => {
                if (!innerField.key) {
                  return innerField;
                }

                return this.enhancedField(
                  innerField.key,
                  innerField,
                  data,
                  readonly,
                  print,
                  innerModel || {}
                );
              }),
            },
            row
          );
        });

        return [
          defaultsDeep(
            {
              props: {
                tableRows: enhancedRows,
              },
            },
            this.enhancedField(key, field, data, readonly, print)
          ),
        ];
      }

      default:
        if (!field.key) {
          return [field];
        }

        return [this.enhancedField(key, field, data, readonly, print)];
    }
  }

  private handleAbstractGroup(
    field: any,
    path: string,
    data: any,
    readonly: boolean,
    print: boolean
  ): FormlyFieldConfigWithExtensions[] {
    if (this.extensionService && field.expressionsId) {
      field = this.extensionService.setExpressions(field, field.expressionsId);
    }

    return map(field.fields, (_field: FormlyFieldConfigWithExtensions) => {
      const expressions =
        !!field.expressions && this.prefixExpressions(field.expressions);

      _field.className = join([field.className, _field.className], ' ');

      if (_field.type === 'template' || _field.template) {
        return this.handleTemplate(_field, field.expressions);
      }

      if (!_field.key) {
        return defaultsDeep({ expressions: field.expressions }, _field);
      }

      mergeWith(
        expressions,
        _field.expressions,
        (fieldExpressions, groupExpressions) =>
          join(compact([fieldExpressions, groupExpressions]), ' || ')
      );

      return defaultsDeep(
        { expressions },
        this.enhancedField(
          `${path}.${_field.key}`,
          _field,
          data,
          readonly,
          print
        )
      );
    });
  }

  private handleTemplate(field: any, expressions: any = {}) {
    const fieldClone = defaultsDeep(
      {
        expressions,
        className: join(compact([field.className, 'form__field']), ' '),
      },
      field
    );

    return this.applyExtensions(fieldClone, {});
  }

  private enhancedField(
    key: string,
    field: FormlyFieldConfigWithExtensions,
    data: any,
    inheritedReadonly: boolean,
    print: boolean,
    innerModel?: any
  ): FormlyFieldConfigWithExtensions {
    const model = innerModel || data;
    const readonlyExceptions = [field.props?.['visualOnly']];
    const hasReadonlyExceptions = some(readonlyExceptions, identity);
    const readonly: boolean =
      !hasReadonlyExceptions &&
      (inheritedReadonly || !!field.props?.['readonly']);

    const _field: FormlyFieldConfigWithExtensions & {
      hooksId: string;
    } = defaultsDeep(
      {
        id: key,
        key,
        className: join(
          compact([
            field.className,
            'form__field',
            readonly && 'lui-form-field--readonly',
          ]),
          ' '
        ),
        defaultValue: get(model, key, field.defaultValue),
        props: {
          readonly,
          print,
        },
        expressions:
          !!field.expressions && this.prefixExpressions(field.expressions),
      },
      field
    );

    return this.applyExtensions(_field, data);
  }

  private applyExtensions(field: FormlyFieldConfigWithExtensions, data: any) {
    let fieldClone = cloneDeep(field);

    if (!this.extensionService) return fieldClone;

    if (fieldClone.extensionIds) {
      fieldClone = this.extensionService.setExtension(
        fieldClone,
        field.extensionIds,
        data
      );
    }

    if (fieldClone.hooksId) {
      fieldClone = this.extensionService.setHooks(
        fieldClone,
        fieldClone.hooksId
      );
    }

    if (fieldClone.parsersId) {
      fieldClone = this.extensionService.setParsers(
        fieldClone,
        fieldClone.parsersId
      );
    }

    if (fieldClone.expressionsId) {
      fieldClone = this.extensionService.setExpressions(
        fieldClone,
        fieldClone.expressionsId
      );
    }

    if (fieldClone.validatorIds) {
      fieldClone = this.extensionService.setValidators(
        fieldClone,
        fieldClone.validatorIds
      );
    }

    if (fieldClone.fieldGroupValidatorId) {
      fieldClone = this.extensionService.setFieldGroupValidator(
        fieldClone,
        fieldClone.fieldGroupValidatorId
      );
    }

    return fieldClone;
  }

  private prefixExpressions(expressions: any): any {
    return mapValues(clone(expressions), (expression) =>
      this.prefixer(expression)
    );
  }

  private setPrefixer(prefix: string) {
    this.prefixer = (expression) => {
      if (!isString(expression)) {
        return expression;
      }
      return expression.replace(
        /(?<=model\?\.)[^?]+(?=\?\.)/g,
        camelCase(prefix)
      );
    };
  }

  private applyExpressionsToFieldGroup(fieldGroup: any): any {
    if (fieldGroup.expressionsId) {
      fieldGroup = this.extensionService.setExpressions(
        fieldGroup,
        fieldGroup.expressionsId
      );
    }

    forEach(fieldGroup.props?.visibility, (_, expressionId) => {
      fieldGroup = this.extensionService.setExpressions(
        fieldGroup,
        camelCase(`sectionVisibility ${expressionId}`)
      );
    });

    return fieldGroup;
  }
}
